import { useEffect, useState, useRef } from 'react'
import { FaCheck, FaExclamation } from "react-icons/fa";
import { IoGitBranch } from "react-icons/io5";
import { getTaskStatus } from '../services/uploadService';
import AssemblyTreeViewer from './AssemblyTreeViewer';

const PHASES = [
  { name: 'Download', icon: '1' },
  { name: 'Scan ZIP', icon: '2' },
  { name: 'Extract & OCR', icon: '3' },
  { name: 'SourceOptima AI', icon: '4' },
  { name: 'Save to Database', icon: '5' },
  { name: 'Complete', icon: '6' },
]

// SourceOptima Corporate Colors
const COLORS = {
  deepTeal: '#1e5045',
  cream: '#f6f4ed',
  orange: '#f26522',
  white: '#fff',
  accentGreen: '#94c43d',
}

const LEVEL_COLORS = {
  phase: 'text-[#f26522] font-bold',      // Orange for phase headers
  success: 'text-[#94c43d]',               // Accent green for success
  info: 'text-gray-400',                   // Gray for info
  warning: 'text-[#f26522]',               // Orange for warnings
  error: 'text-red-400',                   // Red for errors
}

/**
 * Categorize errors for user-friendly messaging
 * Returns an object with message and retryable flag
 */
function categorizeError(error) {
  const errorStr = String(error || '').toLowerCase();

  // S3/Network errors - usually retryable
  if (errorStr.includes('s3_download') ||
      errorStr.includes('s3download') ||
      errorStr.includes('connection') ||
      errorStr.includes('timeout') ||
      errorStr.includes('network')) {
    return {
      message: 'A temporary network issue occurred while processing your files. Please try uploading again.',
      retryable: true,
      type: 'network'
    };
  }

  // Rate limiting / throttling
  if (errorStr.includes('throttl') ||
      errorStr.includes('rate limit') ||
      errorStr.includes('429') ||
      errorStr.includes('quota') ||
      errorStr.includes('slow down')) {
    return {
      message: 'The server is currently busy. Please wait a moment and try again.',
      retryable: true,
      type: 'rate_limit'
    };
  }

  // File format errors - not retryable with same files
  if (errorStr.includes('invalid pdf') ||
      errorStr.includes('corrupt') ||
      errorStr.includes('format') ||
      errorStr.includes('unsupported')) {
    return {
      message: 'One or more files could not be processed. Please check that all files are valid PDFs.',
      retryable: false,
      type: 'format'
    };
  }

  // Access/Permission errors
  if (errorStr.includes('access denied') ||
      errorStr.includes('permission') ||
      errorStr.includes('forbidden') ||
      errorStr.includes('unauthorized')) {
    return {
      message: 'Access error. Please try logging out and back in, then retry.',
      retryable: true,
      type: 'permission'
    };
  }

  // Generic fallback
  return {
    message: error || 'Processing failed. Please try again or contact support if the issue persists.',
    retryable: true,
    type: 'unknown'
  };
}

export default function ProcessingStatus({
  taskId,
  onComplete,
  onError,
  onAssemblyApproval,  // 12-25: Callback for assembly approval
  showAssemblyReview = false  // 12-25: Enable assembly review mode
}) {
  const [progress, setProgress] = useState(null)
  const [status, setStatus] = useState('PENDING')
  const [startTime] = useState(Date.now())
  const logContainerRef = useRef(null)

  // 12-25: Assembly approval state
  const [assemblyTree, setAssemblyTree] = useState(null)
  const [fileIds, setFileIds] = useState([])
  const [isApproving, setIsApproving] = useState(false)

  // Dynamic polling backoff state
  const [pollInterval, setPollInterval] = useState(500)
  const [concurrentUsers, setConcurrentUsers] = useState(0)
  const [isThrottled, setIsThrottled] = useState(false)

  // Auto-scroll log to bottom
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight
    }
  }, [progress?.status_log])

  useEffect(() => {
    if (!taskId) return

    let isMounted = true
    let pollCount = 0
    const MAX_POLLS = 1200 // 10 minutes at 500ms
    const token = sessionStorage.getItem("userToken");
    let currentPollInterval = 500 // Start with default, will be updated dynamically
    let timeoutId = null

    const pollStatus = async () => {
      if (!isMounted) return

      try {
        pollCount++
        console.log(`[ProcessingStatus] Polling task ${taskId} (attempt ${pollCount}, interval ${currentPollInterval}ms)`)

        const data = await getTaskStatus(taskId, token)

        if (!isMounted) return

        // Update dynamic polling backoff state
        if (data.recommended_poll_interval_ms) {
          currentPollInterval = data.recommended_poll_interval_ms
          setPollInterval(data.recommended_poll_interval_ms)
        }
        if (data.concurrent_users !== undefined) {
          setConcurrentUsers(data.concurrent_users)
        }
        if (data.throttled !== undefined) {
          setIsThrottled(data.throttled)
        }

        console.log(`[ProcessingStatus] Task status: ${data.status}`, data)
        setStatus(data.status)

        if (data.status === 'PROGRESS' && data.progress) {
          setProgress(data.progress)
        } else if (data.status === 'SUCCESS') {
          console.log('[ProcessingStatus] Task completed successfully')

          // 12-25: Check if result indicates PENDING_APPROVAL for assembly review
          if (data.result?.status === 'PENDING_APPROVAL' && showAssemblyReview) {
            console.log('[ProcessingStatus] Assembly review required')
            setStatus('PENDING_APPROVAL')
            setAssemblyTree(data.result.assembly_tree)
            setFileIds(data.result.file_ids || [])
            if (data.result?.status_log) {
              setProgress(prev => ({
                ...prev,
                stage: 'pending_approval',
                phase: 4,
                progress_pct: 80,
                status_log: data.result.status_log,
              }))
            }
            return // Don't call onComplete yet - wait for approval
          }

          if (data.result?.status_log) {
            setProgress(prev => ({
              ...prev,
              stage: 'complete',
              phase: 5,
              progress_pct: 100,
              status_log: data.result.status_log,
            }))
          }
          setTimeout(() => onComplete(data.result), 1500)
          return // Stop polling
        } else if (data.status === 'FAILURE' || data.status === 'FAILED') {
          console.error('[ProcessingStatus] Task failed:', data.error)
          // Categorize the error for user-friendly messaging
          const errorInfo = categorizeError(data.error)
          console.log('[ProcessingStatus] Error categorized:', errorInfo)
          onError(errorInfo.message)
          return // Stop polling
        } else if (pollCount >= MAX_POLLS) {
          console.error('[ProcessingStatus] Polling timeout')
          onError('Processing timeout - please check server logs')
          return // Stop polling
        }

        // Schedule next poll with dynamic interval
        timeoutId = setTimeout(pollStatus, currentPollInterval)
      } catch (err) {
        console.error('[ProcessingStatus] Error polling status:', err)
        if (pollCount >= 10) {
          onError('Failed to connect to server')
          return // Stop polling
        }
        // Retry with current interval on error
        timeoutId = setTimeout(pollStatus, currentPollInterval)
      }
    }

    pollStatus()

    return () => {
      isMounted = false
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [taskId, onComplete, onError])

  // 12-25: Handle assembly approval
  const handleAssemblyApprove = async (ids, tree) => {
    if (!onAssemblyApproval) return

    setIsApproving(true)
    try {
      await onAssemblyApproval(ids, tree)
      // After approval, the parent component should start a new task
      // and we'll poll that task for completion
    } catch (err) {
      console.error('[ProcessingStatus] Assembly approval failed:', err)
      onError(err.message || 'Assembly approval failed')
    } finally {
      setIsApproving(false)
    }
  }

  const currentPhase = progress?.phase ?? 0
  const elapsedSeconds = progress?.elapsed_seconds ??
    Math.round((Date.now() - startTime) / 1000)

  // 12-25: Show assembly review UI when in PENDING_APPROVAL status
  if (status === 'PENDING_APPROVAL' && assemblyTree) {
    return (
      <div className="space-y-6">
        {/* Assembly Review Header */}
        <div className="flex items-center gap-3 p-4 bg-[#f26522]/10 border border-[#f26522]/30 rounded-lg">
          <IoGitBranch className="w-6 h-6 text-[#f26522]" />
          <div>
            <h3 className="font-semibold text-[#1e5045]">Assembly Review Required</h3>
            <p className="text-sm text-[#1e5045]/70">
              Review the reconstructed BOM structure before final classification
            </p>
          </div>
        </div>

        {/* Assembly Tree Viewer */}
        <AssemblyTreeViewer
          assemblyTree={assemblyTree}
          onApprove={handleAssemblyApprove}
          isApproving={isApproving}
          fileIds={fileIds}
        />

        {/* Activity Log (collapsed) */}
        <details className="bg-[#f6f4ed] rounded-lg border border-[#1e5045]/10">
          <summary className="px-4 py-2 text-sm font-medium text-[#1e5045] cursor-pointer hover:text-[#1e5045]/80">
            View Activity Log ({progress?.status_log?.length || 0} entries)
          </summary>
          <div
            ref={logContainerRef}
            className="bg-[#1e5045] rounded-b-lg p-3 h-48 overflow-y-auto font-mono text-xs"
          >
            {progress?.status_log?.map((entry, idx) => (
              <div
                key={idx}
                className={`py-0.5 ${LEVEL_COLORS[entry.level] || 'text-gray-400'}`}
              >
                <span className="text-[#f6f4ed]/40 mr-2">
                  [{entry.timestamp.toFixed(1).padStart(6)}s]
                </span>
                {entry.message}
              </div>
            ))}
          </div>
        </details>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Phase Stepper */}
      <div className="flex flex-wrap gap-2">
        {PHASES.map((phase, idx) => {
          let bgColor = 'bg-[#1e5045]/10 text-[#1e5045]/50'
          let iconBg = 'bg-[#1e5045]/20'
          if (idx < currentPhase) {
            bgColor = 'bg-[#94c43d] text-white'
            iconBg = 'bg-white/30'
          } else if (idx === currentPhase) {
            bgColor = 'bg-[#f26522] text-white animate-pulse'
            iconBg = 'bg-white/20'
          }

          return (
            <div
              key={phase.name}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${bgColor}`}
            >
              <span className={`w-4 h-4 rounded-full ${iconBg} flex items-center justify-center text-[10px]`}>
                {idx < currentPhase ? '✓' : phase.icon}
              </span>
              {phase.name}
            </div>
          )
        })}
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#1e5045]/70 truncate max-w-[70%]">
            {progress?.message || 'Initializing...'}
          </span>
          <span className="font-medium text-[#f26522]">
            {elapsedSeconds.toFixed ? elapsedSeconds.toFixed(1) : elapsedSeconds}s
          </span>
        </div>
        <div className="h-2 bg-[#1e5045]/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#f26522] to-[#94c43d] rounded-full transition-all duration-300"
            style={{ width: `${progress?.progress_pct || 0}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-[#1e5045]/50">
          <span>
            {progress?.current !== undefined && progress?.total !== undefined
              ? `${progress.current} / ${progress.total} files`
              : ''}
          </span>
          <span>{Math.round(progress?.progress_pct || 0)}%</span>
        </div>
      </div>

      {/* Activity Log - Terminal Style */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-[#1e5045]">Activity Log</h4>
          {progress?.status_log && (
            <span className="text-xs text-[#1e5045]/40">
              {progress.status_log.length} entries
            </span>
          )}
        </div>
        <div
          ref={logContainerRef}
          className="bg-[#1e5045] rounded-lg p-3 h-64 overflow-y-auto font-mono text-xs"
        >
          {progress?.status_log?.length ? (
            progress.status_log.map((entry, idx) => (
              <div
                key={idx}
                className={`py-0.5 ${LEVEL_COLORS[entry.level] || 'text-gray-400'} ${
                  entry.level === 'phase' ? 'mt-2 first:mt-0' : ''
                }`}
              >
                <span className="text-[#f6f4ed]/40 mr-2">
                  [{entry.timestamp.toFixed(1).padStart(6)}s]
                </span>
                {entry.message}
              </div>
            ))
          ) : (
            <div className="text-[#f6f4ed]/50 flex items-center gap-2">
              <span className="animate-pulse">●</span>
              Waiting for processing to start...
            </div>
          )}
          {/* Throttle notification */}
          {isThrottled && status !== 'SUCCESS' && status !== 'FAILURE' && (
            <div className="py-0.5 text-[#f26522] mt-2">
              <span className="text-[#f6f4ed]/40 mr-2">[SYSTEM]</span>
              High server activity ({concurrentUsers} users) - updates slowed to every {(pollInterval / 1000).toFixed(1)}s
            </div>
          )}
          {/* Blinking cursor */}
          {status !== 'SUCCESS' && status !== 'FAILURE' && (
            <div className="text-[#94c43d] animate-pulse mt-1">▌</div>
          )}
        </div>
      </div>

      {/* Status Indicator */}
      <div className="flex items-center gap-2 text-sm">
        {status === 'SUCCESS' ? (
          <>
            <FaCheck className="w-5 h-5 text-[#94c43d]" />
            <span className="text-[#1e5045] font-medium">
              Processing complete! Redirecting...
            </span>
          </>
        ) : status === 'FAILURE' ? (
          <>
            <FaExclamation className="w-5 h-5 text-red-500" />
            <span className="text-red-600 font-medium">Processing failed</span>
          </>
        ) : (
          <>
            <div className="w-5 h-5 border-2 border-[#f26522] border-t-transparent rounded-full animate-spin" />
            <span className="text-[#1e5045]">
              Processing your files...
              {progress?.successful !== undefined && (
                <span className="ml-2 text-[#1e5045]/50">
                  ({progress.successful} successful
                  {progress.cached > 0 && `, ${progress.cached} cached`}
                  {progress.failed > 0 && `, ${progress.failed} failed`})
                </span>
              )}
            </span>
          </>
        )}
      </div>
    </div>
  )
}
